import {CloseOutlined} from '@ant-design/icons';
import {Button, Divider, Flex, Form, Modal} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, {ReactNode, useContext, useEffect, useState} from 'react';

import {detectAutoDetect, drawerZIndex} from '../../../constants/Common';
import SubmitDisabledContext from '../../../pages/esgManager/constants/SubmitDisabledContext';
import DropdownConfigProvider from '../../DrawerForm/Utils/DropdownConfigProvider';
import Loader from '../../Loader/Loader';

import {uniqueId} from './uniqueId';

interface ModalFormProps {
  open: boolean;
  onCancel: () => void;
  FormComponent: ReactNode;
  title?: string;
  initialValue?: any;
  data?: any;
  idKey?: string;
  buttonText?: string;
  table?: boolean;
  type?: {key: string; value: string} | undefined;
  setDataSource: React.Dispatch<React.SetStateAction<any[]>>;
  dataSource: any[];
  editId: any;
  isEdit?: boolean;
  field: any;
}

const ModalAddNewToTable: React.FC<ModalFormProps> = ({
  open,
  onCancel,
  initialValue,
  data = undefined,
  FormComponent,
  title,
  buttonText = 'Submit',
  setDataSource,
  dataSource,
  editId,
  isEdit,
  field
}) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);

  const setIsSubmitDisabled = useContext(SubmitDisabledContext);

  useEffect(() => {
    if (data !== undefined) {
      form.setFieldsValue(data);
      setIsLoading(false);
      setSubmitDisabled(true);
    } else {
      form.resetFields(initialValue);
      setIsLoading(false);
      setSubmitDisabled(true);
    }
  }, [open, data]);

  const FormInstance = useFormInstance();
  const {setFieldsValue} = FormInstance;

  const onSubmit = (value: any) => {
    Object.keys(value).forEach((key) => {
      if (value[key] === undefined) {
        value[key] = null;
      }
    });
    if (isEdit) {
      const updatedData = dataSource.map((item) => {
        if (item._id === editId) {
          return {...item, ...value};
        }

        return item;
      });
      setDataSource(updatedData);
      setFieldsValue({[field.fieldName]: {...updatedData}});
    } else {
      const newData = [
        ...dataSource,
        {...value, _id: `new${uniqueId(dataSource)}`}
      ];
      setDataSource(newData);
      setFieldsValue({[field.fieldName]: newData});
    }
    onCancel();
    setIsSubmitted(false);
  };

  const onOk = () => {
    form.submit();
    setIsSubmitted(true);
    setIsSubmitDisabled(false);
  };

  return isLoading ? (
    <Loader />
  ) : (
    <Modal
      title={title}
      open={open}
      centered
      okButtonProps={{
        htmlType: 'submit',
        loading: isSubmitted,
        disabled: submitDisabled
      }}
      onCancel={onCancel}
      okText={buttonText}
      maskClosable={false}
      width={'70%'}
      destroyOnClose={true}
      zIndex={drawerZIndex}
      style={{maxHeight: '80vh'}}
      className="esgFormModal"
      footer={
        <Flex vertical>
          <Divider />
          <Flex gap={10} justify="flex-end">
            <Button onClick={onCancel} className="drawerButton drawerCancel">
              Cancel
              <CloseOutlined />
            </Button>
            <Button
              type="primary"
              className="drawerButton confirmSave"
              onClick={onOk}
              loading={isSubmitted}
              htmlType="submit"
            >
              Save
            </Button>
          </Flex>
        </Flex>
      }
    >
      <DropdownConfigProvider>
        <Form
          style={{
            paddingTop: '2rem',
            overflowY: field?.overflow ?? 'auto',
            maxHeight: '70vh'
          }}
          form={form}
          onFinish={onSubmit}
          onFinishFailed={() => {
            setIsSubmitted(false);
          }}
          onValuesChange={() => {
            setSubmitDisabled(false);
          }}
          autoComplete={detectAutoDetect()}
          className="modalForm"
        >
          <div>{FormComponent}</div>
        </Form>
      </DropdownConfigProvider>
    </Modal>
  );
};

export default ModalAddNewToTable;
